import React from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { Box, Typography, IconButton } from '@mui/material';
import { Pencil, Trash2, BarChart2, Clock, FileText } from 'lucide-react';
import { Button, Dialog, EnhancedAccordian } from '../../../../../../ui';
import { AddQuestionModal } from '../../../../../../ui/add_question_modal/add_question_modal';
import { Modal } from '../../../../../../ui/modal/modal';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../i18n/constants/keys';
import {
  Question,
  SubSkill,
} from '../../../../../../utils/types/assessment_types';

import { AutoAssessment } from '../../../../../../utils/types/JobOpenings';
import { SelectOptions } from '../../../../../../utils/types/react_select_types';
import { DIALOG_TYPE } from '../../../../Constants/DialogTypes';
import { DropdownWithModal } from '../../../ui/DropdownWithModal';
import { SaveTemplate } from './Components';
import { TemplateDrawer } from './Components/TemplateDrawer';
import { QuickBuildDrawer } from './Containers';
import { SelectedQuestionContent } from './Containers/Components';
import SelectQuestions from './Containers/SelectQuestions';
import { useQuestions } from './Hooks';

interface IQuestions {
  assessmentId: string;
  skillList?: SelectOptions[];
  levelList?: SelectOptions[];
  jobTitleList?: SelectOptions[];
  subSkillList?: SubSkill[];
  setSkillList: (opts: SelectOptions[]) => void;
  jobTitle: string;
  jobTitleId: string;
  completedElement: () => void;
  setAutoAssessment: React.Dispatch<React.SetStateAction<AutoAssessment>>;
  autoAssessment: AutoAssessment;
  skills: string[];
}

export const Questions: React.FC<IQuestions> = ({
  assessmentId,
  skillList,
  levelList,
  jobTitleList,
  subSkillList,
  setSkillList,
  jobTitle,
  jobTitleId,
  completedElement,
  setAutoAssessment,
  autoAssessment,
  skills,
}) => {
  const { t } = useTranslation(NS.adminPages);
  const SQT = ADMIN_PAGES_KEYS.jobOpenings.skillQuestionsTab;
  const {
    expandedIds,
    questionList,
    editQuestion,
    deleteQuestion,
    handleExpandAll,
    handleOpenSelectQuestions,
    handleDragEnd,
    toggleExpand,
    isOpenSelectQuestions,
    handleCloseSelectQuestions,
    handleAddQuestion,
    selectedQuestions,
    handleSaveSelection,
    totalCount,
    setIsTemplateDrawerOpen,
    setIsTemplateCreateOpen,
    setIsModalOpen,
    toggleAddQuestionModal,
    setDisabled,
    editMap,
    questionTypes,
    isModalOpen,
    handleCloseModal,
    handleDeleteTemplate,
    addQuestionModalOpen,
    addNewQuestion,
    setAddQuestionModalOpen,
    isTemplateDrawerOpen,
    handleTemplateSelect,
    isTemplateCreateOpen,
    handleTemplateSave,
    isOpenQuickBuild,
    handleOpenQuickBuild,
    handleCloseQuickBuild,
    fetchQuestions,
  } = useQuestions(assessmentId, completedElement);

  return (
    <>
      {/* Modern Header */}
      <Box sx={{ pb: { xs: 2, sm: 3 } }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', lg: 'center' },
            gap: 2,
          }}
        >
          {/* Title & Stats Group */}
          <Box sx={{ display: 'flex', width: { xs: '100%', lg: 'auto' }, flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {t(SQT.heading)}
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1, sm: 1.5 }, alignItems: 'center' }}>
              <Box
                sx={{
                  px: 1.5,
                  py: 0.75,
                  bgcolor: 'primary.50',
                  color: 'primary.dark',
                  borderRadius: 2,
                  border: 1,
                  borderColor: 'primary.light',
                  fontWeight: 500,
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <BarChart2 size={16} color="#1976d2" />
                {t(SQT.score)} {totalCount.totalPoints}
              </Box>

              <Box
                sx={{
                  px: 1.5,
                  py: 0.75,
                  bgcolor: 'warning.50',
                  color: 'warning.dark',
                  borderRadius: 2,
                  border: 1,
                  borderColor: 'warning.light',
                  fontWeight: 500,
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Clock size={16} color="#ed6c02" />
                {t(SQT.duration)} {totalCount.totalTime} min
              </Box>

              <Box
                sx={{
                  px: 1.5,
                  py: 0.75,
                  bgcolor: 'secondary.50',
                  color: 'secondary.dark',
                  borderRadius: 2,
                  border: 1,
                  borderColor: 'secondary.light',
                  fontWeight: 500,
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <FileText size={16} color="#9c27b0" />
                {questionList.length} {t(SQT.questions)}
              </Box>
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              width: { xs: '100%', sm: 'auto' },
              alignItems: 'center',
              justifyContent: { sm: 'flex-end' },
              gap: { xs: 1, sm: 1.5 },
              alignSelf: { lg: 'center' },
            }}
          >
            <DropdownWithModal
              label={t(SQT.actions)}
              sx={{ gridColumn: { xs: "span 2", sm: "span 1" }, minWidth: { sm: 120 } }}
              buttonColor="gray"
              options={[
                {
                  label: t(SQT.loadTemplate),
                  onClick: () => setIsTemplateDrawerOpen(true),
                  icon: (
                    <Box
                      component="svg"
                      sx={{ width: 16, height: 16, color: 'text.secondary' }}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                      />
                    </Box>
                  ),
                  value: 'load-template',
                },
                {
                  label: t(SQT.createTemplate),
                  onClick: () => setIsTemplateCreateOpen(true),
                  icon: (
                    <Box
                      component="svg"
                      sx={{ width: 16, height: 16, color: 'text.secondary' }}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
                      />
                    </Box>
                  ),
                  value: 'create-template',
                },
                {
                  label: t(SQT.removeAllQuestions),
                  onClick: () => {
                    setIsModalOpen(true);
                  },
                  icon: (
                    <Box
                      component="svg"
                      sx={{ width: 16, height: 16, color: 'error.main' }}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </Box>
                  ),
                  value: 'remove-all',
                  hoverStyle: 'red',
                },
                {
                  label:
                    expandedIds.length === questionList.length
                      ? t(SQT.collapseAll)
                      : t(SQT.expandAll),
                  onClick: handleExpandAll,
                  icon: (
                    <Box
                      component="svg"
                      sx={{ width: 16, height: 16, color: 'text.secondary' }}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      {expandedIds.length === questionList.length ? (
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7-7-7 7m14 14l-7-7-7 7"
                        />
                      ) : (
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 15l7-7 7 7m-14-4l7 7 7-7"
                        />
                      )}
                    </Box>
                  ),
                  value: 'toggle-expand',
                },
              ]}
            />
            <Button
              label={t(SQT.selectQuestions)}
              onClick={handleOpenSelectQuestions}
              variant="link"
              sx={{ width: { xs: "100%", sm: "auto" }, bgcolor: "#f9fafb", color: "#374151", borderColor: "#e5e7eb", "&:hover": { bgcolor: "#f3f4f6" }, boxShadow: 1 }}
            />
            <Button
              label={t(SQT.quickBuild)}
              onClick={handleOpenQuickBuild}
              variant="link"
              sx={{ width: { xs: "100%", sm: "auto" }, boxShadow: 2, "&:hover": { boxShadow: 4 } }}
            />
            <Button
              label={t(SQT.createNew)}
              onClick={toggleAddQuestionModal}
              variant="primary"
              sx={{ gridColumn: { xs: "span 2", sm: "span 1" }, width: { xs: "100%", sm: "auto" }, boxShadow: 2, "&:hover": { boxShadow: 4 } }}
            />
          </Box>
        </Box>
      </Box>

      {/* List */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="questions">
          {(prov) => (
            <Box {...prov.droppableProps} ref={prov.innerRef}>
              {questionList.map((q, i) => {
                const isDisabled = editMap[q.id] ?? true;

                const onEditSave = (updated: Question) => {
                  setDisabled(q.id, true);
                  editQuestion(updated);
                };

                const returnSelectedJobTitles = () =>
                  (q.jobTitle ?? [])
                    .map((id) => {
                      const found = jobTitleList?.find((j) => j.value === id);
                      return found
                        ? { value: found.value, label: found.label }
                        : null;
                    })
                    .filter((x): x is SelectOptions => Boolean(x));

                return (
                  <Draggable key={q.id} draggableId={q.id} index={i}>
                    {(p) => (
                      <Box
                        ref={p.innerRef}
                        {...p.draggableProps}
                        {...p.dragHandleProps}
                        sx={{ mb: 1.5 }}
                      >
                        <EnhancedAccordian
                          title={q.question}
                          isExpanded={expandedIds.includes(q.id)}
                          onToggle={() => toggleExpand(q.id)}
                          rightIcons={[
                            <IconButton
                              key="edit"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDisabled(q.id, false);
                                if (!expandedIds.includes(q.id))
                                  toggleExpand(q.id);
                              }}
                            >
                              <Pencil size={16} />
                            </IconButton>,
                            <IconButton
                              key="delete"
                              size="small"
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                q.assessmentQuestionId &&
                                  deleteQuestion(q.assessmentQuestionId);
                              }}
                            >
                              <Trash2 size={16} />
                            </IconButton>,
                          ]}
                        >
                          <SelectedQuestionContent
                            toggledQuestion={q}
                            isDisabled={isDisabled}
                            setIsDisabled={(d) => setDisabled(q.id, d)}
                            editQuestion={onEditSave}
                            onAddNewSkill={() => {}}
                            returnSelectedJobTitles={returnSelectedJobTitles}
                            skills={skillList || []}
                            subSkills1={
                              subSkillList
                                ?.filter((s) => s.parentSkillId === q.skillId)
                                .map((s) => ({
                                  value: s.id,
                                  label: s.subskillName,
                                })) || []
                            }
                            subSkills2={
                              subSkillList
                                ?.filter(
                                  (s) => s.parentSkillId === q.subSkill1Id,
                                )
                                .map((s) => ({
                                  value: s.id,
                                  label: s.subskillName,
                                })) || []
                            }
                            subSkills3={
                              subSkillList
                                ?.filter(
                                  (s) => s.parentSkillId === q.subSkill2Id,
                                )
                                .map((s) => ({
                                  value: s.id,
                                  label: s.subskillName,
                                })) || []
                            }
                            levelList={levelList}
                            jobTitleList={jobTitleList || []}
                            questionTypes={questionTypes}
                          />
                        </EnhancedAccordian>
                      </Box>
                    )}
                  </Draggable>
                );
              })}
              {prov.placeholder}
            </Box>
          )}
        </Droppable>
      </DragDropContext>
      <SelectQuestions
        isOpen={isOpenSelectQuestions}
        onCloseDrawer={handleCloseSelectQuestions}
        onSave={handleSaveSelection}
        onAddQuestion={handleAddQuestion}
        initialSelected={selectedQuestions}
        assessmentId={assessmentId}
        skillList={skillList}
        levelList={levelList}
        subSkillList={subSkillList}
        jobTitleList={jobTitleList}
      />
      <QuickBuildDrawer
        assessmentId={assessmentId}
        isOpen={isOpenQuickBuild}
        onCloseDrawer={handleCloseQuickBuild}
        autoAssessment={autoAssessment}
        setAutoAssessment={setAutoAssessment}
        skills={skills}
        jobTitle={jobTitle}
        completedElement={completedElement}
        onGenerationComplete={fetchQuestions}
      />
      <Dialog
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={t(SQT.deleteAllConfirm)}
        dialogType={DIALOG_TYPE.ERROR}
        justify="center"
        confirmButtonLable={t(SQT.deleteAll)}
        cancelButtonLable={t(SQT.cancel)}
        handleApply={handleDeleteTemplate}
      >
        <></>
      </Dialog>
      <Modal
        visible={addQuestionModalOpen}
        modalContent={
          addQuestionModalOpen ? (
            <AddQuestionModal
              addNewQuestion={addNewQuestion}
              skillList={skillList || []}
              levelList={levelList || []}
              jobTitleList={jobTitleList || []}
              subSkillList={subSkillList}
              setSkillList={setSkillList}
            />
          ) : (
            <></>
          )
        }
        toggle={() => setAddQuestionModalOpen(false)}
      />
      <TemplateDrawer
        isOpen={isTemplateDrawerOpen}
        onClose={() => setIsTemplateDrawerOpen(false)}
        onSelectTemplate={handleTemplateSelect}
      />
      <Modal
        headerText={t(SQT.saveTemplate)}
        visible={isTemplateCreateOpen}
        modalContent={
          <SaveTemplate
            closeModal={() => setIsTemplateCreateOpen(false)}
            onTemplateSave={handleTemplateSave}
            questions={questionList}
            jobTitle={jobTitle}
            jobTitleId={jobTitleId}
            assessmentSettings={{
              attemptCount: '1',
              customMessage: '',
              enableCopy: false,
              showScore: true,
              shuffleQuestions: false,
              blurVideo: false,
            }}
          />
        }
        toggle={() => setIsTemplateCreateOpen(false)}
      />
    </>
  );
};
export default Questions;
